import { ImageModel, ImageModelCatalog } from "../types/MeGenTypes";

/**
 * Helper ล้วน ๆ รอบ catalog โมเดล image (GET /media/image-models)
 *
 * กติกา: ไม่มีชื่อโมเดลอยู่ในโค้ดหน้าบ้าน — list/default/ความสามารถ/เพดานรูป มาจาก catalog เท่านั้น
 * `catalog = null` (ยังไม่โหลด/โหลดไม่สำเร็จ) ต้องไม่ทำให้ซ่อนหรือลบข้อมูลของ user และต้องไม่บล็อกการ generate
 */

// ใช้เมื่อยังไม่รู้ cap จริง — จำกัดแบบ conservative ให้ไม่เกิน Qwen 2.0's provider cap.
// ต้องตรงกับ default ของ ImageAssetsPanel (`props.maxSelected || 3`).
export const FALLBACK_REFERENCE_CAP = 3;

// scene ที่ helper ชุดนี้สนใจ (subset ของ ScriptWithImageItem)
export type ImageModelScene = {
  image_model?: string;
  reference_images?: string[];
};

/** ค้นทั้ง list รวม model ที่ enabled = false เพื่อ resolve label/cap ของ id เก่าที่ item ถืออยู่ */
export const findImageModel = (
  id: string | undefined | null,
  catalog: ImageModelCatalog | null | undefined,
): ImageModel | undefined => {
  if (!id || !catalog?.models?.length) return undefined;
  return catalog.models.find((model) => model.id === id);
};

/** model ที่เอาไปทำ dropdown ได้ */
export const enabledModels = (catalog: ImageModelCatalog | null | undefined): ImageModel[] =>
  (catalog?.models || []).filter((model) => model.enabled);

/** ป้ายของ option ว่าง — โชว์ id ของ default จาก catalog เมื่อรู้ */
export const defaultLabel = (catalog: ImageModelCatalog | null | undefined): string =>
  catalog?.default ? `Use Global Default (${catalog.default})` : "Use Global Default";

/** โมเดลที่หลังบ้านจะใช้จริงกับ scene นี้ — undefined = ยังไม่รู้ (ไม่ได้เลือก + ไม่มี catalog) */
export const effectiveImageModel = (
  item: ImageModelScene,
  catalog: ImageModelCatalog | null | undefined,
): string | undefined => item.image_model || catalog?.default || undefined;

/**
 * โมเดลนี้รับรูปอ้างอิงได้ไหม
 * `null` = ตอบไม่ได้ (ไม่มี catalog / ไม่รู้จัก id) — ผู้เรียกต้องตัดสินใจแบบไม่ทำลายข้อมูล
 */
export const modelSupportsReferences = (
  id: string | undefined | null,
  catalog: ImageModelCatalog | null | undefined,
): boolean | null => {
  const model = findImageModel(id, catalog);
  if (!model) return null;
  const hasRefMode = (model.modes || []).some((mode) => mode === "edit" || mode === "ref");
  // cap 0 = t2i อย่างเดียว แม้จะประกาศ mode ไว้ (กัน Panel fallback เป็น 3 เงียบ ๆ)
  return hasRefMode && model.max_input_images > 0;
};

/** เพดานรูปอ้างอิงต่อ scene — ไม่รู้จัก/cap 0 → fallback 3 */
export const referenceCap = (
  id: string | undefined | null,
  catalog: ImageModelCatalog | null | undefined,
): number => findImageModel(id, catalog)?.max_input_images || FALLBACK_REFERENCE_CAP;

/**
 * รูปอ้างอิงของ scene ที่ควรเหลือหลังเปลี่ยนโมเดล
 * `null` = ไม่ต้องแตะ (ไม่มี catalog / ไม่รู้จัก id / ไม่มีรูป / ยังไม่เกิน cap)
 * `[]` = โมเดลที่จะใช้จริงไม่รองรับรูปอ้างอิง
 * `refs.slice(0, cap)` = รองรับแต่เกินเพดาน — ตัดท้ายออก คงลำดับเดิม (ลำดับใช้อ้างใน Scene Description)
 */
export const clampReferences = (
  item: ImageModelScene,
  catalog: ImageModelCatalog | null | undefined,
): string[] | null => {
  const refs = item.reference_images || [];
  if (refs.length === 0) return null;

  const modelId = effectiveImageModel(item, catalog);
  const supports = modelSupportsReferences(modelId, catalog);
  if (supports === null) return null; // ตอบไม่ได้ → ห้ามลบของ user
  if (!supports) return [];

  const cap = referenceCap(modelId, catalog);
  return refs.length > cap ? refs.slice(0, cap) : null;
};

/** โชว์ panel รูปอ้างอิงของ scene นี้ไหม — ตอบไม่ได้ ⇒ ยึดว่ามีรูปอยู่แล้วหรือไม่ */
export const sceneAllowsReferences = (
  item: ImageModelScene,
  catalog: ImageModelCatalog | null | undefined,
): boolean =>
  modelSupportsReferences(effectiveImageModel(item, catalog), catalog) ??
  (item.reference_images?.length || 0) > 0;
